import { Link, router } from '@inertiajs/react'
import { AlertCircle } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { BackofficeComplianceFiling } from '@/types'

type Props = {
  filings: BackofficeComplianceFiling[]
  search: string
  status_filter: string
  pagination: PaginationMeta
}


export default function ComplianceFilingsIndex({ filings, search, status_filter, pagination }: Props) {
  return (
    <BackofficeLayout title="Compliance Filings" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Compliance Filings' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Compliance Filings</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? 'filing' : 'filings'}</p>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <SearchInput value={search} placeholder="Search filings..." />
        <select
          value={status_filter}
          onChange={e => router.get('/backoffice/compliance_filings', { search, status: e.target.value }, { preserveState: true })}
          className="h-9 text-sm border rounded-md px-3"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="mt-4">
        {filings.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No compliance filings found</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {filings.map((f, i) => {
              const isOverdue = f.next_due_date && new Date(f.next_due_date) < new Date()
              return (
                <Link
                  key={f.id}
                  href={`/backoffice/compliance_filings/${f.id}`}
                  className={`block px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{f.type_name}</span>
                        <StatusBadge status={f.status} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {f.account_name} · {f.business_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOverdue && f.status === 'active' && (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      {f.latest_record_stage && <StageBadge stage={f.latest_record_stage} variant="compliance" />}
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
